// The normative shape of ICD-S08 Fact Store — the read side.
//
// Everything under `packages/info` so far has been about getting data IN. This
// is the seam it comes back out of, and the whole system is worth nothing until
// something can ask it a question.
//
// Three rules shape this file, and all three are invariants in the ICD:
//
//   1. **Provenance survives.** There is no read path that loses `source`,
//      `observedAt` or `confidence`. A number on a screen can always be traced
//      to who said it and when we heard.
//   2. **Unknown is a value.** Never null, never zero, never an empty array. A
//      player with no night-game history and a calculation we have not run yet
//      are different answers and a screen must be able to render them
//      differently.
//   3. **A dossier reports its own incompleteness.** Returning seven sections
//      and letting the caller assume there were only ever seven is how a
//      missing weather source becomes a silent lie.
//
// Read-only, absolutely. Writes go through the acquisition runner; there is no
// method here that mutates anything, and conformance check 18 proves it by
// running the same query twice and comparing the database byte for byte.

import { z } from 'zod';
import type { Role } from '../kinds/registry';

/** How a caller names an entity: our id, or any vendor key we hold. */
export type EntityRef = { id: string } | { namespace: string; key: string };

export const Entity = z.object({
  id: z.string().min(1),
  type: z.enum(['person', 'team', 'venue', 'contest', 'org']),
  sport: z.string().nullable(),
  league: z.string().nullable(),
  display: z.string().min(1),
  /**
   * Every vendor key we hold. The swap mechanism — see ICD-P01.
   *
   * OPTIONAL, AND OMITTED FROM A DOSSIER BY DEFAULT. Measured on a 92-player
   * team page: the key maps were **68 KB of 418 KB raw and 7.8 KB of the 23.6 KB
   * that actually crossed the wire** — a third of the compressed payload — and
   * nothing in `apps/` or `functions/` has ever read one. They are how a vendor
   * id becomes our id, which is a question the ingest side asks and a screen
   * never does.
   *
   * `entity()` still returns them, because a single lookup is where you ask what
   * an entity is known as, and one map costs nothing. `context()` and
   * `contextMany()` include them only when `ContextOptions.includeKeys` says so.
   */
  keys: z.record(z.string(), z.string()).optional(),
});
export type Entity = z.infer<typeof Entity>;

/**
 * Who told us, and under what terms. Travels with every fact.
 *
 * Zod rather than a bare interface because a dossier crosses the wire and the
 * app on the other side parses it. "Validate at the boundary" cuts both ways:
 * an endpoint that emits a shape nobody checks is one refactor away from
 * handing a screen a field it does not have.
 */
export const SourceRef = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  license: z.string().min(1),
  /** Rendered verbatim wherever the fact is shown. Null = none required. */
  attribution: z.string().nullable(),
});
export type SourceRef = z.infer<typeof SourceRef>;

/** A fact as a caller sees it: parsed, validated, and carrying its provenance. */
export const Fact = z.object({
  id: z.string().min(1),
  kind: z.string().min(1),
  /** Parsed against the schema registered for `kind`, never raw JSON. Typed there. */
  payload: z.unknown(),
  validFrom: z.string().min(1),
  /** Null means STILL TRUE. Distinct from unknown, always. */
  validTo: z.string().nullable(),
  observedAt: z.string().min(1),
  source: SourceRef,
  confidence: z.enum(['official', 'reported', 'derived', 'estimated']),
  /** Set when a correction exists and `asOf` is after it. */
  supersededBy: z.string().nullable(),
  /**
   * What this fact is about, and how — layer 4, traveling with the fact.
   *
   * Without it a caller holding a squad’s injury reports cannot say whose they
   * are except by reading a vendor key out of the payload and knowing which
   * namespace it belongs to, which is source-specific knowledge no screen
   * should need. The ids here are OURS, and `SectionResult.entities` carries
   * the entities they name.
   */
  refs: z.array(z.object({ entityId: z.string().min(1), role: z.string().min(1) })),
  sport: z.string().nullable(),
  league: z.string().nullable(),
  /** Null where the fact is not period-bound — a venue's roof is not about a week. */
  season: z.number().int().nullable(),
  period: z.number().int().nullable(),
});
export type Fact = z.infer<typeof Fact>;

/**
 * Why an answer is absent.
 *
 * Never `null`, never `0`, never `[]`. The four reasons are different problems
 * with different fixes, and collapsing them loses the difference between "we
 * have not built this yet" and "this player has never played a night game".
 */
export interface Unknown {
  unknown: true;
  reason: 'no-spec' | 'not-computed' | 'insufficient-data' | 'source-unavailable';
  detail?: string;
}

export const unknown = (reason: Unknown['reason'], detail?: string): Unknown =>
  detail === undefined ? { unknown: true, reason } : { unknown: true, reason, detail };

export interface FeatureValue {
  value: unknown;
  /** WHICH calculation produced this. Never omitted — a number without its
   * method is not reproducible, and every feature is our opinion, not a fact. */
  specId: string;
  specVersion: number;
  computedAt: string;
  /** What it was computed over, so a screen can say "12 night games since 2021". */
  basis: { facts: number; from: string; to: string };
}

export const DOSSIER_SECTIONS = [
  'identity',
  'membership',
  'contest',
  'own-facts',
  'teammates',
  'opponent',
  'environment',
  'features',
  'news',
] as const;

export type DossierSection = (typeof DOSSIER_SECTIONS)[number];

export const SectionResult = z.object({
  facts: z.array(Fact),
  /** Entities this section is about, so a caller need not re-resolve them. */
  entities: z.array(Entity),
});
export type SectionResult = z.infer<typeof SectionResult>;

/** A section that could not be assembled, and why. Never silently absent. */
export const Incompleteness = z.object({
  section: z.enum(DOSSIER_SECTIONS),
  /**
   * Why the section is not here, as a code a caller can branch on.
   *
   * `bye` earns its own value rather than folding into `no-data` because the
   * two mean opposite things to a manager: a bye is a COMPLETE answer — their
   * team is not playing, so the slot cannot score, which CLAUDE.md requires a
   * screen to say out loud — while `no-data` means we do not know. The
   * distinction was carried in the English of `detail` until the simulator was
   * asked to produce the same view and had no way to say it. A caller matching
   * prose is a caller that breaks when the prose improves.
   */
  reason: z.enum([
    'no-spec',
    'not-computed',
    'insufficient-data',
    'source-unavailable',
    'no-data',
    'bye',
  ]),
  detail: z.string().min(1),
});
export type Incompleteness = z.infer<typeof Incompleteness>;

/** Every source that contributed, for a license obligation discharged structurally. */
export const Attribution = z.object({
  sourceId: z.string().min(1),
  name: z.string().min(1),
  license: z.string().min(1),
  text: z.string().nullable(),
  url: z.string().nullable(),
});
export type Attribution = z.infer<typeof Attribution>;

export const Dossier = z.object({
  entity: Entity,
  /** What we knew at this instant. Echoed so a caller can cache against it. */
  asOf: z.string().min(1),
  season: z.number().int(),
  period: z.number().int(),
  /**
   * Only the sections that were asked for AND filled. Anything missing is in
   * `incomplete` with a reason, which is why this is partial rather than a
   * record with nine possibly-empty slots.
   */
  sections: z.partialRecord(z.enum(DOSSIER_SECTIONS), SectionResult),
  attribution: z.array(Attribution),
  incomplete: z.array(Incompleteness),
});
export type Dossier = z.infer<typeof Dossier>;

export interface ContextOptions {
  season: number;
  period: number;
  /** Which sections to assemble. Default: all. */
  include?: readonly DossierSection[];
  /** What we knew at this instant. Default: now. */
  asOf?: string;
  /**
   * Carry `Entity.keys` on the entities in a dossier. Default false.
   *
   * Off by default because no screen reads them and they are a third of the
   * compressed payload of a squad. On for an admin surface that genuinely wants
   * to see what a vendor calls somebody.
   */
  includeKeys?: boolean;
  /** How far to traverse. 1 is the entity, its team and its contest. */
  depth?: 1 | 2;
}

export interface FactQuery {
  entities?: EntityRef[];
  roles?: Role[];
  kinds?: string[];
  topics?: string[];
  sport?: string;
  league?: string;
  season?: number;
  period?: number;
  /** Facts whose validity window intersects this range. */
  validDuring?: { from: string; to: string };
  asOf?: string;
  /** Keyset, never offset — ingest inserts constantly and offset skips rows. */
  cursor?: string;
  limit?: number;
}

export interface Page<T> {
  rows: T[];
  /** Null means this is the last page. Opaque to the caller. */
  nextCursor: string | null;
}

export interface Change {
  fact: Fact;
  kind: 'appeared' | 'superseded' | 'ended';
  previous: Fact | null;
  observedAt: string;
}

export interface ChangeOptions {
  kinds?: string[];
  entities?: EntityRef[];
  limit?: number;
  cursor?: string;
}

/**
 * The read seam. Implemented twice on purpose.
 *
 * Conformance check 17 — "two implementations return the same dossier shape for
 * the same fixture" — is what earns `SimFactStore`. With one implementation an
 * interface is a description of whatever D1 happened to make easy; the second
 * is what turns it into a contract.
 */
export interface FactStore {
  /** The dossier. One call, assembled here, because nine round trips is not a screen. */
  context(ref: EntityRef, opts: ContextOptions): Promise<Dossier>;

  /**
   * The same work for many entities, batched.
   *
   * Not in the original ICD, and added for a plain reason: a team page
   * needs its whole squad and must not be fifty-three dossiers. Implementations
   * must do this as one set of queries across all the entities, not a loop over
   * `context` — the point is the query count, and a convenience wrapper that
   * loops would be a lie that only shows up under load.
   */
  contextMany(refs: readonly EntityRef[], opts: ContextOptions): Promise<Dossier[]>;

  /** The general query, for everything `context` does not anticipate. */
  facts(query: FactQuery): Promise<Page<Fact>>;

  /** Our calculations. Returns `Unknown` rather than guessing. */
  feature(
    specId: string,
    ref: EntityRef,
    at: { season: number; period: number },
  ): Promise<FeatureValue | Unknown>;

  /** The changelog. P-05 Player News is largely this with a presentation layer. */
  changes(since: string, opts?: ChangeOptions): Promise<Page<Change>>;

  entity(ref: EntityRef): Promise<Entity | null>;

  resolve(namespace: string, key: string): Promise<EntityRef | null>;
}

// ── Views: the right amount of information for the situation ────────────────
//
// A thorough dossier for a player, plus ways to pull just the level of detail
// a situation needs — a team page versus a player page.
//
// `include` already expresses this, but leaving every caller to invent its own
// section list has two costs: the same page shape gets spelled three different
// ways across the app, and nobody can tell what a call is going to cost by
// reading it. A view is a NAME for a section list, so cost is legible at the
// call site and the same page always asks for the same thing.
//
// Views are DATA, not code — the same rule as league rules. Adding one is a row
// here, and no implementation changes.

export type PlayerView = 'card' | 'roster' | 'dossier';

export interface ViewSpec {
  sections: readonly DossierSection[];
  depth: 1 | 2;
  /** What this view is for, in one line. Shown in the API's own error text. */
  because: string;
}

export const PLAYER_VIEWS: Record<PlayerView, ViewSpec> = {
  /**
   * A row in a list, a chip on a lineup. Who they are, where they play, and
   * whether anything is wrong with them. One entity, no traversal.
   */
  card: {
    sections: ['identity', 'membership', 'own-facts'],
    depth: 1,
    because: 'a row in a list: identity, team, and their own status',
  },

  /**
   * A team page. Everything `card` has, plus the fixture the squad is playing,
   * because a roster is read in the context of the next game. Still depth 1:
   * fifty-three players must not each drag in fifty-two teammates.
   */
  roster: {
    sections: ['identity', 'membership', 'own-facts', 'contest'],
    depth: 1,
    because: 'a squad list read against the next fixture',
  },

  /**
   * The decision page. Everything, including the teammates and the weather —
   * the sections that cost a traversal and are worth it exactly once.
   */
  dossier: {
    sections: DOSSIER_SECTIONS,
    depth: 2,
    because: 'one player, everything we hold, for a decision',
  },
};

export function isPlayerView(v: string): v is PlayerView {
  return Object.prototype.hasOwnProperty.call(PLAYER_VIEWS, v);
}

/** Turn a view name into the options the store takes. */
export function viewOptions(
  view: PlayerView,
  at: { season: number; period: number; asOf?: string },
): ContextOptions {
  const spec = PLAYER_VIEWS[view];
  return {
    season: at.season,
    period: at.period,
    include: spec.sections,
    depth: spec.depth,
    ...(at.asOf === undefined ? {} : { asOf: at.asOf }),
  };
}
